import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import toLower from 'lodash/toLower';
import toString from 'lodash/toString';
import trim from 'lodash/trim';
import keys from 'lodash/keys';
import first from 'lodash/first';
import map from 'lodash/map';
import includes from 'lodash/includes';
import { formatKilometers, getMiles } from '../globals/globals';
import { DISTANCE_UNIT } from './../const/distance.const';
import { FILE_TYPES } from './../const/file.const';
import { FALLBACK_MESSAGE } from './../const/message.const';
import { DELIMITERS } from '../const/text.const';
/**
 * Evaluates a given string value if it's a null string
 * @param value string
 * @returns boolean
 */
export function isNullString(value) {
    return toLower(value) === 'null';
}
/**
 * Evaluates a given string value if it's an undefined string
 * @param value string
 * @returns boolean
 */
export function isUndefinedString(value) {
    return toLower(value) === 'undefined';
}
/**
 * Evaluates a given string value if it's a null of undefined (nil) as string
 * @param value string
 * @returns boolean
 */
export function isNilString(value) {
    return toLower(value) === 'null' || value === 'undefined';
}
/**
 * Evaluates a given string value or boolean if it's a boolean string
 * @param value string | boolean
 * @returns boolean
 */
export function isBooleanString(value) {
    if (isBoolean(value)) {
        return false;
    }
    const lowercased = toLower(value);
    return isBoolean(value) || (lowercased === 'false' || lowercased === 'true');
}
/**
 * Evaluates a given value if it's a falsy in disguise
 * @param value boolean | string | number | null | undefined
 * @returns boolean
 */
export function isFalsyString(value) {
    const falsyValues = ['false', 'undefined', 'null', '0', '', ''];
    const stringValue = toLower(toString(value));
    const isFalsy = falsyValues.includes(stringValue);
    return (isBoolean(value) && !value) || isFalsy;
}
/**
 * Converts the given object to boolean.
 * @param {any} value The provided object value to convert.
 * @returns {boolean} A boolean value corresponding to the given object.
 */
export function toBoolean(value) {
    if (!value) {
        return false;
    }
    const parseBoolConfig = {
        // truthy
        true: true,
        yes: true,
        y: true,
        on: true,
        1: true,
        // falsy
        false: false,
        no: false,
        n: false,
        off: false,
        0: false,
    };
    return !isNaN(value) && isNumber(value)
        ? value > 0
        : parseBoolConfig[trim(toString(value).toLowerCase())];
}
/**
 * Saves the payload provided as a downloaded file
 * @param {any} content The provided object or array to save
 * @param {any} filename Desired file name
 * @param {any} type File type
 */
export function exportFile(content, filename = 'Exported data', type = FILE_TYPES.csv) {
    const blob = new Blob([content], { type });
    if (navigator.msSaveBlob) {
        navigator.msSaveBlob(blob, filename);
    }
    else {
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }
}
/**
 * Converts an array of objects to CSV
 * @param list array of objects we want to convert to CSV
 * @param fallbackMessage custom text to use on undefined/null values, default: 'No data'
 * @returns string
 */
export function getCsvContent(list, fallbackMessage = FALLBACK_MESSAGE.noData) {
    if (isEmpty(list)) {
        return '';
    }
    const exportKeys = keys(first(list));
    return `${exportKeys.join()}\n${map(list, row => exportKeys
        .map(key => {
        if (isNil(row[key])) {
            return fallbackMessage;
        }
        return includes(toString(row[key]), DELIMITERS.comma) ? `"${row[key]}"` : row[key];
    })
        .join())
        .join('\n')}`;
}
/**
 * Formats a distance value
 * @param value number, string - distance in kilometers
 * @param unit string
 * @param precision numnber - represeniting desired outcome precission
 * @returns string
 */
export function formatDistance(value, unit, precision = 3) {
    const distance = unit === DISTANCE_UNIT.miles
        ? getMiles(value, precision, unit)
        : formatKilometers(value, precision, unit);
    return distance;
}
/**
 * Formats a vehicle's speed with automatic recalculation for miles
 * @param value number, string - numeric value of the speed
 * @param unit string - speed unit with default as 'km'
 * @param precision numnber - formatting precision
 * @returns string
 */
export function formatSpeed(value, unit = DISTANCE_UNIT.kilometers, precision = 0) {
    const numericValue = isNumber(value) ? value : parseInt(value, 10);
    if (isNaN(numericValue) || !numericValue) {
        return `0 ${unit}/h`;
    }
    const speed = unit === DISTANCE_UNIT.miles
        ? getMiles(numericValue, precision, unit)
        : formatKilometers(numericValue, precision, unit);
    return `${speed}/h`;
}
